import { Children, ComponentType, ReactElement, ReactNode } from 'react';

import {
  attachComponentData,
  getComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

export const DIALOG_SLOT_KEY = 'dialogComposite.slot';

export type DialogSlot =
  | 'header'
  | 'actions'
  | 'title'
  | 'subtitle'
  | 'closeButton';

export const asDialogSlot = <P>(
  component: ComponentType<P>,
  slot: DialogSlot,
): ComponentType<P> => {
  attachComponentData(component, DIALOG_SLOT_KEY, slot);
  return component;
};

const getSlot = (node: ReactNode): DialogSlot | undefined =>
  getComponentData<DialogSlot>(node, DIALOG_SLOT_KEY);

export interface DialogSlots {
  dialogTitle?: ReactNode;
  subtitle?: ReactNode;
  headerExtras: ReactNode[];
  body: ReactNode[];
  footer?: ReactNode;
}

export const useDialogSlots = (children: ReactNode): DialogSlots =>
  useElementFilter(children, elements => {
    const slots: DialogSlots = { headerExtras: [], body: [] };

    elements.getElements<{ children?: ReactNode }>().forEach(element => {
      switch (getSlot(element)) {
        case 'header':
          Children.forEach(element.props.children, headerChild => {
            switch (getSlot(headerChild)) {
              case 'title':
                slots.dialogTitle = (
                  headerChild as ReactElement<{ children?: ReactNode }>
                ).props.children;
                break;
              case 'subtitle':
                if (!slots.subtitle) {
                  slots.subtitle = headerChild;
                }
                break;
              // CloseButton is deprecated; drop it so unmigrated call sites
              // don't render a stray button until they remove it.
              case 'closeButton':
                break;
              default:
                slots.headerExtras.push(headerChild);
            }
          });
          break;
        case 'actions':
          slots.footer = element;
          break;
        default:
          slots.body.push(element);
      }
    });

    return slots;
  });
